import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-base font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 tracking-[0.1px] leading-6",
  {
    variants: {
      variant: {
        default: "bg-[#E7FE52] text-black hover:bg-[#E7FE52]/90 shadow-[0px_1px_2px_rgba(10,12.67,18,0.05)]",
        secondary: "border-2 border-[#F0F0F0] bg-[#FDFDFD] text-black hover:bg-[#FDFDFD]/90 rounded-[8px] h-12 w-[152px] px-5 py-3 gap-[10px]",
        destructive: "bg-[#F2F2F2] text-black hover:bg-[#F2F2F2]/90 rounded-[8px] h-12 w-[115px] px-6 py-3 gap-[10px]",
        link: "text-black underline-offset-4 hover:underline",
      },
      size: {
        default: "h-12 w-[147px] px-6 py-3",
        wide: "h-12 w-[315px] px-[18px] py-[10px]",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
